import React, { KeyboardEvent, useState, useCallback, useRef } from 'react';
import Select, { components, MultiValue, InputActionMeta, ActionMeta } from 'react-select';
import styles from './EmailMultiSelect.module.scss';

interface EmailOption {
  label: string;
  value: string;
}

interface EmailMultiSelectProps {
  emails: string[];
  onChange: (emails: string[]) => void;
  placeholder?: string;
  validateEmail?: (email: string) => boolean;
  getLabel?: (email: string, index: number, removeEmail: (index: number) => void) => React.ReactNode;
  disabled?: boolean;
  className?: string;
  onBlur?: () => void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EmailMultiSelect: React.FC<EmailMultiSelectProps> = ({
  emails,
  onChange,
  placeholder = 'Enter email addresses...',
  validateEmail = (email: string) => EMAIL_REGEX.test(email),
  getLabel,
  disabled = false,
  className = '',
  onBlur,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const selectRef = useRef<any>(null);

  // Convert string array to react-select option format
  const emailOptions: EmailOption[] = emails.map((email) => ({
    label: email,
    value: email,
  }));

  // Helper function to create email tag from input
  const createEmailFromInput = useCallback(
    (input: string) => {
      const trimmedEmail = input.trim();
      if (trimmedEmail && !emails.includes(trimmedEmail)) {
        const newEmails = [...emails, trimmedEmail];
        onChange(newEmails);
      }
      setInputValue('');
    },
    [emails, onChange]
  );

  // Handle input change and keyboard events
  const handleInputChange = useCallback(
    (newValue: string, actionMeta: InputActionMeta) => {
      if (actionMeta.action === 'input-change') {
        const lastChar = newValue.slice(-1);

        // Create email on comma, semicolon, or space
        if ([',', ';', ' '].includes(lastChar)) {
          const emailToAdd = newValue.slice(0, -1).trim();
          if (emailToAdd) {
            createEmailFromInput(emailToAdd);
            return;
          }
        }

        setInputValue(newValue);
      }
    },
    [createEmailFromInput]
  );

  // Handle key down events
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === 'Tab') {
        if (inputValue.trim()) {
          event.preventDefault();
          createEmailFromInput(inputValue);
        }
      }

      // Handle backspace to remove last email when input is empty
      if (event.key === 'Backspace' && !inputValue && emails.length > 0) {
        const newEmails = emails.slice(0, -1);
        onChange(newEmails);
      }
    },
    [inputValue, createEmailFromInput, emails, onChange]
  );

  // Handle selection changes (removal via react-select interface)
  const handleChange = useCallback(
    (selectedOptions: MultiValue<EmailOption>, actionMeta: ActionMeta<EmailOption>) => {
      const newEmails = selectedOptions.map((option) => option.value);
      onChange(newEmails);
    },
    [onChange]
  );

  // Handle blur event - commit any pending input value
  const handleBlur = useCallback(() => {
    if (inputValue.trim()) {
      createEmailFromInput(inputValue);
    }
    // Call parent onBlur if provided
    onBlur?.();
  }, [inputValue, createEmailFromInput, onBlur]);

  // Custom MultiValueLabel component for email tags
  const MultiValueLabel = useCallback(
    (props: any) => {
      const { data, index } = props;
      const removeEmail = (indexToRemove: number) => {
        const newEmails = emails.filter((_, i) => i !== indexToRemove);
        onChange(newEmails);
      };

      // Use custom getLabel if provided, otherwise use default
      if (getLabel) {
        const emailIndex = emails.findIndex((email) => email === data.value);
        return <div className={styles.customTag}>{getLabel(data.value, emailIndex, removeEmail)}</div>;
      }

      return <components.MultiValueLabel {...props} />;
    },
    [emails, onChange, getLabel]
  );

  // Custom MultiValueRemove component
  const MultiValueRemove = useCallback(
    (props: any) => {
      // If using custom getLabel, the remove functionality is handled there
      if (getLabel) {
        return null;
      }
      return <components.MultiValueRemove {...props} />;
    },
    [getLabel]
  );

  // Focus the select when clicked
  const handleContainerClick = useCallback(() => {
    if (selectRef.current && !disabled) {
      selectRef.current.focus();
    }
  }, [disabled]);

  // Custom styles for react-select
  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      border: 'none',
      boxShadow: 'none',
      backgroundColor: 'transparent',
      minHeight: 'auto',
      '&:hover': {
        border: 'none',
      },
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      padding: '0',
      gap: '0.14285714em',
      flexWrap: 'wrap' as const,
    }),
    multiValue: (provided: any) => ({
      ...provided,
      margin: '0.14285714em',
      backgroundColor: '#f3f3f3',
      borderRadius: '0.28571429rem',
    }),
    multiValueLabel: (provided: any) => ({
      ...provided,
      color: 'rgba(0, 0, 0, 0.6)',
      fontWeight: 600,
      fontSize: '0.8rem',
      padding: '0.5833em 0.833em',
    }),
    multiValueRemove: (provided: any) => ({
      ...provided,
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
      },
    }),
    input: (provided: any) => ({
      ...provided,
      margin: '0',
      padding: '0.4em 0.1em',
    }),
    indicatorsContainer: () => ({
      display: 'none',
    }),
    menu: () => ({
      display: 'none', // Hide dropdown menu since we're not using options
    }),
  };

  // Determine if container should show invalid state
  const hasInvalidEmails = emails.some((email) => !validateEmail(email));
  const isInputInvalid = inputValue.trim() && !validateEmail(inputValue.trim());
  const showInvalidState = hasInvalidEmails || isInputInvalid;

  return (
    <div
      className={`${styles.emailMultiSelect} ${className} ${showInvalidState ? styles.invalid : ''} ${isMenuOpen ? styles.focused : ''}`}
      onClick={handleContainerClick}
    >
      <Select
        ref={selectRef}
        isMulti
        value={emailOptions}
        onChange={handleChange}
        onInputChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        inputValue={inputValue}
        placeholder=""
        isDisabled={disabled}
        menuIsOpen={false} // Never show the dropdown menu
        onMenuOpen={() => setIsMenuOpen(true)}
        onMenuClose={() => setIsMenuOpen(false)}
        components={{
          MultiValueLabel,
          MultiValueRemove,
          DropdownIndicator: () => null,
          ClearIndicator: () => null,
        }}
        styles={customStyles}
        blurInputOnSelect={false}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        tabSelectsValue={false}
        controlShouldRenderValue={true}
      />
      {!emailOptions.length && !inputValue && (
        <span className={styles.placeholder} data-placeholder>
          {placeholder}
        </span>
      )}
    </div>
  );
};

export default EmailMultiSelect;
